import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, useReducedMotion, type Variants } from 'motion/react';
import PageHeader from './shared/PageHeader';
import { useAuthStore } from '../stores/useAuthStore';
import { BillingService, CreditPackage, BillingTransaction, SubscriptionService, PackagesResponse } from '../services/billingService';
import { SubscriptionPlan, CurrentSubscription } from '../types/plan';
import UserCreditsDashboard from './billing/UserCreditsDashboard';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { Button, Input, Select, Card, Badge } from './ui';
import PrivacySettingsCard from './shared/legal/PrivacySettingsCard';
import type { SelectOption } from './ui';

// Page-load entrance variants — mirrors LoginPage Phase 5 pattern.
// Numeric durations match --snapfy-duration-base (300 ms) and --snapfy-duration-slow (500 ms);
// easing mirrors --snapfy-ease-standard.
const pageVariants: Variants = {
    hidden: { opacity: 0, y: 16 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
    },
} as const;

const CreditPackageItem: React.FC<{
    pkg: CreditPackage;
    isSelected: boolean;
    onSelect: () => void;
}> = ({ pkg, isSelected, onSelect }) => {
    return (
        <div
            onClick={onSelect}
            className={`bg-snapfy-inner rounded-card p-6 border-2 transition-all cursor-pointer relative ${isSelected ? 'border-snapfy-primary ring-2 ring-snapfy-primary/50' : 'border-snapfy-border hover:border-snapfy-border'
                }`}
        >
            {pkg.isPopular && (
                <div className="absolute -top-3 right-4 bg-snapfy-primary text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                    Popular
                </div>
            )}
            <h3 className="text-2xl font-bold text-snapfy-text-primary">{pkg.credits.toLocaleString()}</h3>
            <p className="text-snapfy-text-secondary">{pkg.credits === 1 ? 'crédito' : 'créditos'}</p>
            <p className="text-4xl font-extrabold text-snapfy-text-primary my-4">
                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pkg.price)}
            </p>
            <ul className="text-sm space-y-2 text-snapfy-text-secondary">
                <li>Aproximadamente {pkg.imageCount} imagens</li>
                <li>
                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(pkg.pricePerImage)} por imagem
                </li>
                {pkg.discount && <li className="font-semibold text-snapfy-success">{pkg.discount} de desconto</li>}
            </ul>
        </div>
    );
};

const CreditInfoItem: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <li>
        <strong className="font-semibold text-snapfy-text-primary">{title}:</strong>{' '}
        <span className="text-snapfy-text-secondary">{children}</span>
    </li>
);

const ProfilePage: React.FC = () => {
    const { user } = useAuthStore();
    const [searchParams] = useSearchParams();
    const prefersReducedMotion = useReducedMotion();
    const isCompanyNonAdmin = !!(user as any)?.companyId && (user as any)?.companyRole !== 'admin';
    const tabFromUrl = searchParams.get('tab') as 'credits' | 'purchase' | 'history' | null;
    const defaultTab = isCompanyNonAdmin ? 'credits' : (tabFromUrl || 'purchase');
    const [activeTab, setActiveTab] = useState<'credits' | 'purchase' | 'history' | 'security'>(defaultTab);
    const [packages, setPackages] = useState<CreditPackage[]>([]);
    const [history, setHistory] = useState<BillingTransaction[]>([]);
    const [selectedPackageId, setSelectedPackageId] = useState<string>('');
    const [isLoadingPackages, setIsLoadingPackages] = useState(false);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [canPurchaseBundles, setCanPurchaseBundles] = useState(true);

    // Subscription plans state
    const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
    const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
    const [isLoadingPlans, setIsLoadingPlans] = useState(false);
    const [isSubscribing, setIsSubscribing] = useState<string | null>(null);

    // Password change state
    const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', newPasswordConfirmation: '' });
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

    const handleChangePassword = async () => {
        setPasswordErrors({});
        if (passwordData.newPassword.length < 8) {
            setPasswordErrors({ newPassword: 'A nova senha deve ter no mínimo 8 caracteres.' });
            return;
        }
        if (passwordData.newPassword !== passwordData.newPasswordConfirmation) {
            setPasswordErrors({ newPasswordConfirmation: 'As senhas não coincidem.' });
            return;
        }
        setIsChangingPassword(true);
        try {
            await api.put('/user/password', passwordData);
            toast.success('Senha alterada com sucesso!');
            setPasswordData({ currentPassword: '', newPassword: '', newPasswordConfirmation: '' });
        } catch (error: unknown) {
            const err = error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } };
            if (err.response?.data?.errors) {
                const mapped: Record<string, string> = {};
                for (const [key, msgs] of Object.entries(err.response.data.errors)) {
                    mapped[key] = (msgs as string[])[0];
                }
                setPasswordErrors(mapped);
            } else {
                toast.error(err.response?.data?.message || 'Erro ao alterar senha.');
            }
        } finally {
            setIsChangingPassword(false);
        }
    };

    // Funções de máscara
    const formatCPF = (value: string) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) {
            return numbers
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d)/, '$1.$2')
                .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
        }
        return numbers.slice(0, 11)
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d)/, '$1.$2')
            .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
    };

    const formatPhone = (value: string) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 11) {
            return numbers
                .replace(/(\d{2})(\d)/, '($1) $2')
                .replace(/(\d{5})(\d)/, '$1-$2');
        }
        return numbers.slice(0, 11)
            .replace(/(\d{2})(\d)/, '($1) $2')
            .replace(/(\d{5})(\d)/, '$1-$2');
    };

    // History filters and pagination
    const [historyFilters, setHistoryFilters] = useState({
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
        status: '' as string,
    });
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(1);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const historyPerPage = 10;

    // Billing modal states
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [billingData, setBillingData] = useState({
        document: '',
        phone: '',
        postalCode: '',
        address: '',
        addressNumber: '',
        addressComplement: '',
        neighborhood: '',
        city: '',
        state: '',
    });
    const [isSavingBilling, setIsSavingBilling] = useState(false);
    const [isLoadingCep, setIsLoadingCep] = useState(false);

    // Verificar se usuário está habilitado para NF (feature flag @snapfy.ai)
    const canReceiveInvoice = user?.canReceiveInvoice ?? false;

    // Check if user has complete billing info (including address for NF)
    // Só exige dados completos se usuário for elegível para NF
    const hasCompleteBillingInfo = !canReceiveInvoice || (user?.document && user?.phone && user?.postalCode && user?.address && user?.city);

    // Função para buscar endereço pelo CEP
    const fetchAddressByCep = async (cep: string) => {
        const cleanCep = cep.replace(/\D/g, '');
        if (cleanCep.length !== 8) return;

        setIsLoadingCep(true);
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
            const data = await response.json();
            if (!data.erro) {
                setBillingData(prev => ({
                    ...prev,
                    address: data.logradouro || '',
                    neighborhood: data.bairro || '',
                    city: data.localidade || '',
                    state: data.uf || '',
                }));
            }
        } catch (error) {
            console.error('Erro ao buscar CEP:', error);
        } finally {
            setIsLoadingCep(false);
        }
    };

    // Máscara de CEP
    const formatCEP = (value: string) => {
        const numbers = value.replace(/\D/g, '');
        if (numbers.length <= 8) {
            return numbers.replace(/(\d{5})(\d)/, '$1-$2');
        }
        return numbers.slice(0, 8).replace(/(\d{5})(\d)/, '$1-$2');
    };

    // Use effective balance (company balance when user belongs to a company)
    const totalCredits = user?.effectiveCreditsBalance ?? user?.creditBalance ?? 0;
    // Assuming user object has creditBalance. If not, we might need to fetch it.
    // For now, let's assume creditBalance is available or we default to 0.
    // Wait, we added creditBalance to User type, but backend might not return it in /auth/me yet.
    // We should probably fetch it from a dedicated endpoint if it's dynamic.

    // Let's assume we fetch credits from a separate endpoint or it's part of user.
    // For the chart, we need "used" vs "total".
    // Usually "balance" is what remains. "Total" is lifetime or monthly?
    // The design shows "Restantes" (Remaining) and "Utilizados" (Used).
    // If we only have balance, we can't calculate "used" without knowing "total ever purchased".
    // For now, let's mock "used" as 0 or hide the chart logic if we lack data.
    // Or better, just show Balance.

    const remainingCredits = user?.effectiveCreditsBalance ?? user?.creditBalance ?? 0;
    // Mocking total for visualization if we don't have it
    const mockTotal = remainingCredits + (user?.tokenUsage || 0);
    const usedCredits = user?.tokenUsage || 0;
    const usedPercentage = mockTotal > 0 ? (usedCredits / mockTotal) * 100 : 0;
    const circumference = 2 * Math.PI * 45;

    useEffect(() => {
        const loadPackages = async () => {
            setIsLoadingPackages(true);
            try {
                const result = await BillingService.getPackages();
                setPackages(result.packages);
                setCanPurchaseBundles(result.canPurchase);
                if (result.packages.length > 0) {
                    setSelectedPackageId(result.packages[1]?.id || result.packages[0].id);
                }
            } catch (error) {
                console.error('Failed to load packages', error);
                toast.error('Erro ao carregar pacotes');
            } finally {
                setIsLoadingPackages(false);
            }
        };
        loadPackages();
    }, []);

    useEffect(() => {
        const loadSubscriptionPlans = async () => {
            setIsLoadingPlans(true);
            try {
                const [plans, subscription] = await Promise.all([
                    SubscriptionService.getPlans(),
                    SubscriptionService.getCurrentSubscription(),
                ]);
                // Filter out free/business plans (those are for companies)
                setSubscriptionPlans(plans.filter(p => p.price > 0));
                setCurrentSubscription(subscription);
            } catch (error) {
                console.error('Failed to load subscription plans', error);
            } finally {
                setIsLoadingPlans(false);
            }
        };
        loadSubscriptionPlans();
    }, []);

    const handleSubscribe = async (planId: string) => {
        if (!hasCompleteBillingInfo) {
            setShowBillingModal(true);
            return;
        }
        setIsSubscribing(planId);
        try {
            const checkoutUrl = await SubscriptionService.subscribe(planId);
            window.location.href = checkoutUrl;
        } catch (error: any) {
            const message = error.response?.data?.error || 'Erro ao iniciar assinatura';
            toast.error(message);
            setIsSubscribing(null);
        }
    };

    const handleCancelSubscription = async () => {
        const sub = currentSubscription?.subscription;
        const daysLeft = sub?.daysRemaining ?? 0;
        const endsAt = sub?.endsAt ? new Date(sub.endsAt).toLocaleDateString('pt-BR') : '';
        const confirmMsg = `Tem certeza que deseja cancelar sua assinatura?\n\nVoce ainda tem ${daysLeft} dias restantes (ate ${endsAt}).\nApos o cancelamento, voce mantera acesso ate o final do periodo.`;
        if (!confirm(confirmMsg)) return;
        try {
            const result = await SubscriptionService.cancelSubscription();
            toast.success(result.message);
            const subscription = await SubscriptionService.getCurrentSubscription();
            setCurrentSubscription(subscription);
        } catch (error: any) {
            const message = error.response?.data?.error || 'Erro ao cancelar assinatura';
            toast.error(message);
        }
    };

    const formatCurrency = (value: number, currency: string = 'BRL') => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(value);
    };

    const getFeatureLabel = (key: string, value: string | number | boolean | undefined): string | null => {
        if (value === undefined || value === false) return null;
        const labels: Record<string, string> = {
            maxResolution: `Resolução até ${value}`,
            maxPhotos: `Até ${value} fotos/mês`,
            maxVideos: `Até ${value} vídeos/mês`,
            maxEdits: `Até ${value} edições/mês`,
            maxBrandbooks: `${value} brandbook${Number(value) > 1 ? 's' : ''}`,
            support: `Suporte ${value}`,
            brandbook: 'Brandbook incluso',
            apiAccess: 'Acesso à API',
            dedicatedManager: 'Gerente dedicado',
        };
        return labels[key] || null;
    };

    // Load history with filters
    const loadHistory = async () => {
        setIsLoadingHistory(true);
        try {
            const result = await BillingService.getHistory({
                page: historyPage,
                perPage: historyPerPage,
                startDate: historyFilters.startDate,
                endDate: historyFilters.endDate,
                status: historyFilters.status || undefined,
            });
            setHistory(result.data || result);
            setHistoryTotalPages(result.meta?.lastPage || 1);
        } catch (error) {
            console.error('Failed to load history', error);
            setHistory([]);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'history') {
            loadHistory();
        }
    }, [activeTab, historyPage, historyFilters]);

    const handleSaveBillingInfo = async () => {
        setIsSavingBilling(true);
        const wasCompleteBefore = hasCompleteBillingInfo;
        try {
            // Converter para snake_case para o backend
            const payload = {
                document: billingData.document,
                phone: billingData.phone,
                postal_code: billingData.postalCode,
                address: billingData.address,
                address_number: billingData.addressNumber,
                address_complement: billingData.addressComplement || null,
                neighborhood: billingData.neighborhood,
                city: billingData.city,
                state: billingData.state,
            };
            const response = await api.put('/auth/billing-info', payload);
            useAuthStore.getState().setUser(response.data.user);
            setShowBillingModal(false);
            toast.success('Dados atualizados com sucesso!');
            // Só procede com a compra se estava incompleto antes (vindo do fluxo de compra)
            if (!wasCompleteBefore && selectedPackageId) {
                proceedWithPurchase();
            }
        } catch (error: any) {
            const message = error.response?.data?.message || 'Erro ao atualizar dados';
            toast.error(message);
        } finally {
            setIsSavingBilling(false);
        }
    };

    const proceedWithPurchase = async () => {
        if (!selectedPackageId) return;
        setIsPurchasing(true);
        try {
            const checkoutUrl = await BillingService.purchasePackage(selectedPackageId);
            window.location.href = checkoutUrl;
        } catch (error) {
            console.error('Purchase failed', error);
            toast.error('Erro ao iniciar pagamento');
            setIsPurchasing(false);
        }
    };

    const handlePurchase = async () => {
        if (!canPurchaseBundles) {
            toast.error('Usuários do plano gratuito não podem comprar créditos avulsos. Assine um plano primeiro.');
            return;
        }
        if (!hasCompleteBillingInfo) {
            setShowBillingModal(true);
            return;
        }
        proceedWithPurchase();
    };

    // History status filter options
    const historyStatusOptions: SelectOption[] = [
        { value: '', label: 'Todos' },
        { value: 'consumption', label: 'Consumo' },
        { value: 'purchase', label: 'Compra' },
        { value: 'initial_grant', label: 'Bônus inicial' },
        { value: 'admin_grant', label: 'Crédito manual' },
        { value: 'subscription_grant', label: 'Assinatura' },
    ];

    // Billing state (UF) options
    const ufOptions: SelectOption[] = [
        { value: '', label: 'UF' },
        { value: 'AC', label: 'AC' },
        { value: 'AL', label: 'AL' },
        { value: 'AP', label: 'AP' },
        { value: 'AM', label: 'AM' },
        { value: 'BA', label: 'BA' },
        { value: 'CE', label: 'CE' },
        { value: 'DF', label: 'DF' },
        { value: 'ES', label: 'ES' },
        { value: 'GO', label: 'GO' },
        { value: 'MA', label: 'MA' },
        { value: 'MT', label: 'MT' },
        { value: 'MS', label: 'MS' },
        { value: 'MG', label: 'MG' },
        { value: 'PA', label: 'PA' },
        { value: 'PB', label: 'PB' },
        { value: 'PR', label: 'PR' },
        { value: 'PE', label: 'PE' },
        { value: 'PI', label: 'PI' },
        { value: 'RJ', label: 'RJ' },
        { value: 'RN', label: 'RN' },
        { value: 'RS', label: 'RS' },
        { value: 'RO', label: 'RO' },
        { value: 'RR', label: 'RR' },
        { value: 'SC', label: 'SC' },
        { value: 'SP', label: 'SP' },
        { value: 'SE', label: 'SE' },
        { value: 'TO', label: 'TO' },
    ];

    if (!user) {
        return <div className="text-snapfy-text-primary text-center mt-10">Carregando perfil...</div>;
    }

    // Resolved entrance variants — prefers-reduced-motion guard per DESIGN-CRAFT §1
    const resolvedPageVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1, y: 0 } : pageVariants.hidden,
        visible: prefersReducedMotion
            ? { opacity: 1, y: 0 }
            : pageVariants.visible,
    } as const;

    return (
        <motion.div
            className="space-y-8 pb-10"
            initial="hidden"
            animate="visible"
            variants={resolvedPageVariants}
        >
            <PageHeader
                title="Perfil & Conta"
                titleClassName="!text-4xl !font-bold"
                subtitle="Gerencie suas informações e o uso de créditos."
            />

            {/* Account Details Card */}
            <Card className="p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-snapfy-text-primary">Dados da Conta</h2>
                    {canReceiveInvoice && (
                        <Button
                            variant="solid"
                            onClick={() => {
                                setBillingData({
                                    document: user.document || '',
                                    phone: user.phone || '',
                                    postalCode: user.postalCode || '',
                                    address: user.address || '',
                                    addressNumber: user.addressNumber || '',
                                    addressComplement: user.addressComplement || '',
                                    neighborhood: user.neighborhood || '',
                                    city: user.city || '',
                                    state: user.state || '',
                                });
                                setShowBillingModal(true);
                            }}
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Editar Dados NF
                        </Button>
                    )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                        <label className="text-sm font-medium text-snapfy-text-secondary">Nome</label>
                        <p className="text-lg text-snapfy-text-primary">{user.name}</p>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-snapfy-text-secondary">Email</label>
                        <p className="text-lg text-snapfy-text-primary">{user.email}</p>
                    </div>
                    <div>
                        <label className="text-sm font-medium text-snapfy-text-secondary">Plano</label>
                        <p className="text-lg text-snapfy-text-primary">{user.role === 'admin' ? 'Admin' : 'Standard'}</p>
                    </div>
                    {/* Campos de NF - só aparecem para usuários elegíveis */}
                    {canReceiveInvoice && (
                        <>
                            <div>
                                <label className="text-sm font-medium text-snapfy-text-secondary">CPF</label>
                                <p className="text-lg text-snapfy-text-primary">{user.document ? formatCPF(user.document) : 'Não informado'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-snapfy-text-secondary">Telefone</label>
                                <p className="text-lg text-snapfy-text-primary">{user.phone ? formatPhone(user.phone) : 'Não informado'}</p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-snapfy-text-secondary">Endereço</label>
                                <p className="text-lg text-snapfy-text-primary">
                                    {user.address
                                        ? `${user.address}, ${user.addressNumber || 'S/N'}${user.addressComplement ? ` - ${user.addressComplement}` : ''}`
                                        : 'Não informado'}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-snapfy-text-secondary">Cidade/UF</label>
                                <p className="text-lg text-snapfy-text-primary">
                                    {user.city ? `${user.city}/${user.state}` : 'Não informado'}
                                </p>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-snapfy-text-secondary">Nota Fiscal</label>
                                <p className="text-lg text-snapfy-success flex items-center gap-2">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    Habilitado
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </Card>

            {/* Tabs Navigation — re-styled with tokens + Chip pattern (no Tabs extension created;
                see SUMMARY for rationale). Active tab indicated by Chip selected state +
                bottom border on the wrapper for visual continuity. */}
            <div className="border-b border-snapfy-border">
                <nav className="flex gap-2">
                    {!isCompanyNonAdmin && (
                        <button
                            onClick={() => setActiveTab('purchase')}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'purchase'
                                    ? 'border-snapfy-primary text-snapfy-accent-soft'
                                    : 'border-transparent text-snapfy-text-secondary hover:text-snapfy-text-primary'
                                }`}
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Comprar Créditos
                        </button>
                    )}
                    <button
                        onClick={() => setActiveTab('credits')}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'credits'
                                ? 'border-snapfy-primary text-snapfy-accent-soft'
                                : 'border-transparent text-snapfy-text-secondary hover:text-snapfy-text-primary'
                            }`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        Meus Créditos
                    </button>
                    <button
                        onClick={() => setActiveTab('history')}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'history'
                                ? 'border-snapfy-primary text-snapfy-accent-soft'
                                : 'border-transparent text-snapfy-text-secondary hover:text-snapfy-text-primary'
                            }`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Histórico
                    </button>
                    <button
                        onClick={() => setActiveTab('security')}
                        className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'security'
                                ? 'border-snapfy-primary text-snapfy-accent-soft'
                                : 'border-transparent text-snapfy-text-secondary hover:text-snapfy-text-primary'
                            }`}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Segurança
                    </button>
                </nav>
            </div>

            {/* Credits Dashboard Tab */}
            {activeTab === 'credits' && (
                <>
                    {isCompanyNonAdmin && (
                        <div className="bg-snapfy-primary/10 border border-snapfy-primary/30 rounded-card p-4 mb-6">
                            <p className="text-snapfy-accent-soft text-sm">
                                Você faz parte de uma empresa. Seus créditos são gerenciados pelo administrador da organização.
                                Para mais créditos, entre em contato com o admin da sua empresa.
                            </p>
                        </div>
                    )}
                    <UserCreditsDashboard />
                </>
            )}

            {/* Purchase Tab — hidden for non-admin company members */}
            {activeTab === 'purchase' && !isCompanyNonAdmin && (
                <>
                    {/* Current Subscription Banner */}
                    {currentSubscription?.hasSubscription && currentSubscription.subscription && (
                        <div className="bg-gradient-to-r from-snapfy-primary/10 to-indigo-900/40 rounded-card p-6 border border-snapfy-primary/30">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <svg className="w-5 h-5 text-snapfy-accent-soft" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <h3 className="text-lg font-semibold text-snapfy-text-primary">
                                            Plano {currentSubscription.subscription.plan.name}
                                        </h3>
                                        {currentSubscription.subscription.isCanceled && (
                                            <Badge variant="warning">Cancelado</Badge>
                                        )}
                                    </div>
                                    <p className="text-snapfy-text-secondary text-sm">
                                        {currentSubscription.subscription.isCanceled
                                            ? `Acesso até ${new Date(currentSubscription.subscription.endsAt!).toLocaleDateString('pt-BR')} (${currentSubscription.subscription.daysRemaining} dias restantes)`
                                            : `${formatCurrency(currentSubscription.subscription.plan.price)}/mês — ${currentSubscription.subscription.plan.monthlyCredits.toLocaleString()} créditos/mês`
                                        }
                                    </p>
                                </div>
                                {!currentSubscription.subscription.isCanceled && (
                                    <Button
                                        variant="ghost"
                                        onClick={handleCancelSubscription}
                                        className="border-snapfy-error/30 text-snapfy-error hover:border-snapfy-error hover:text-snapfy-error"
                                    >
                                        Cancelar Assinatura
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Subscription Plans Section */}
                    {subscriptionPlans.length > 0 && !currentSubscription?.hasSubscription && (
                        <Card className="p-8">
                            <h2 className="text-2xl font-semibold text-snapfy-text-primary text-center mb-2">Planos de Assinatura</h2>
                            <p className="text-snapfy-text-secondary text-center mb-8">Créditos mensais recorrentes com benefícios exclusivos</p>
                            {isLoadingPlans ? (
                                <div className="text-center text-snapfy-text-secondary">Carregando planos...</div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {subscriptionPlans.filter(p => !p.adminOnly).map(plan => (
                                        <div
                                            key={plan.id}
                                            className={`relative bg-snapfy-inner rounded-card p-6 border-2 transition-all ${plan.isPopular ? 'border-snapfy-primary ring-2 ring-snapfy-primary/50' : 'border-snapfy-border'}`}
                                        >
                                            {plan.isPopular && (
                                                <div className="absolute -top-3 right-4 bg-snapfy-primary text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                                                    Recomendado para voce
                                                </div>
                                            )}
                                            <h3 className="text-xl font-bold text-snapfy-text-primary mb-1">{plan.name}</h3>
                                            <p className="text-snapfy-text-secondary text-sm mb-4">
                                                {plan.monthlyCredits.toLocaleString()} creditos por mes
                                            </p>
                                            <p className="text-3xl font-extrabold text-snapfy-text-primary mb-1">
                                                {formatCurrency(plan.price)}
                                                <span className="text-base font-normal text-snapfy-text-secondary"> /mes</span>
                                            </p>
                                            {plan.monthlyCredits > 0 && (
                                                <p className="text-snapfy-accent-soft text-sm mb-6">
                                                    Apenas {formatCurrency(plan.price / plan.monthlyCredits)} por geracao
                                                </p>
                                            )}

                                            {/* Features */}
                                            {plan.features && (
                                                <ul className="space-y-2 mb-6">
                                                    {Array.isArray(plan.features)
                                                        ? plan.features.map((feat: string, i: number) => (
                                                            <li key={i} className="flex items-center gap-2 text-sm text-snapfy-text-secondary">
                                                                <svg className="w-4 h-4 text-snapfy-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                                {feat}
                                                            </li>
                                                        ))
                                                        : Object.entries(plan.features).map(([key, value]) => {
                                                            const label = getFeatureLabel(key, value as string | number | boolean | undefined);
                                                            if (!label) return null;
                                                            return (
                                                                <li key={key} className="flex items-center gap-2 text-sm text-snapfy-text-secondary">
                                                                    <svg className="w-4 h-4 text-snapfy-success flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                    </svg>
                                                                    {label}
                                                                </li>
                                                            );
                                                        })
                                                    }
                                                </ul>
                                            )}

                                            <Button
                                                variant={plan.isPopular ? 'primary' : 'ghost'}
                                                onClick={() => handleSubscribe(plan.id)}
                                                disabled={isSubscribing !== null}
                                                className="w-full"
                                            >
                                                {isSubscribing === plan.id ? 'Processando...' : 'Assinar'}
                                            </Button>
                                        </div>
                                    ))}

                                    {/* Enterprise — fixed "Falar com Sales" card */}
                                    <div className="relative bg-snapfy-inner rounded-card p-6 border-2 border-snapfy-border transition-all flex flex-col">
                                        <h3 className="text-xl font-bold text-snapfy-text-primary mb-1">Enterprise</h3>
                                        <p className="text-snapfy-text-secondary text-sm mb-4">Volume personalizado para grandes marcas</p>
                                        <p className="text-3xl font-extrabold text-snapfy-text-primary mb-1">Sob consulta</p>
                                        <p className="text-snapfy-text-secondary text-sm mb-6">Volumes acima de 500 creditos/mes</p>

                                        <ul className="space-y-2 mb-6 flex-1">
                                            <li className="flex items-center gap-2 text-sm text-snapfy-text-secondary">
                                                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Volume personalizado
                                            </li>
                                            <li className="flex items-center gap-2 text-sm text-snapfy-text-secondary">
                                                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Resolucao ate 4K
                                            </li>
                                            <li className="flex items-center gap-2 text-sm text-snapfy-text-secondary">
                                                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Key Account dedicado
                                            </li>
                                            <li className="flex items-center gap-2 text-sm text-snapfy-text-secondary">
                                                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                                Acesso a API
                                            </li>
                                        </ul>

                                        {/* SANCTIONED RESIDUAL: #25D366 / #1ebe57 = WhatsApp brand green — no token exists; Phase 7 */}
                                        <a
                                            href={`https://web.whatsapp.com/send/?phone=11952134358&text=${encodeURIComponent(`Olá! Vim pelo site da Snapfy e tenho interesse no plano Enterprise.\n\nNome: ${user?.name || 'Usuário'}\nID: ${user?.id || '-'}`)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-full py-3 font-semibold rounded-card bg-[#25D366] text-white hover:bg-[#1ebe57] transition-all text-center flex items-center justify-center gap-2"
                                        >
                                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                            </svg>
                                            Falar com Vendas
                                        </a>
                                    </div>
                                </div>
                            )}
                        </Card>
                    )}

                    {/* Divider between plans and bundles */}
                    {subscriptionPlans.length > 0 && !currentSubscription?.hasSubscription && (
                        <div className="flex items-center gap-4">
                            <div className="flex-1 h-px bg-snapfy-inner"></div>
                            <span className="text-snapfy-text-secondary text-sm">ou compre créditos avulsos</span>
                            <div className="flex-1 h-px bg-snapfy-inner"></div>
                        </div>
                    )}

                    {/* Free plan restriction banner */}
                    {!canPurchaseBundles && (
                        <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-card p-6 flex items-start gap-4">
                            <svg className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <div>
                                <h3 className="text-snapfy-warning font-semibold mb-1">Compra de créditos indisponível</h3>
                                <p className="text-snapfy-text-secondary text-sm">
                                    Usuários do plano gratuito não podem comprar pacotes de créditos avulsos.
                                    Assine um dos planos acima para desbloquear a compra de créditos adicionais.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Packages Section */}
                    <Card className={`p-8 ${!canPurchaseBundles ? 'opacity-50 pointer-events-none' : ''}`}>
                        <h2 className="text-2xl font-semibold text-snapfy-text-primary text-center mb-8">Comprar pacotes</h2>
                        {isLoadingPackages ? (
                            <div className="text-center text-snapfy-text-secondary">Carregando pacotes...</div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                {packages.map(pkg => (
                                    <CreditPackageItem
                                        key={pkg.id}
                                        pkg={pkg}
                                        isSelected={selectedPackageId === pkg.id}
                                        onSelect={() => setSelectedPackageId(pkg.id)}
                                    />
                                ))}
                            </div>
                        )}
                        <div className="text-center">
                            <Button
                                variant="cta"
                                onClick={handlePurchase}
                                disabled={isPurchasing || !selectedPackageId}
                                isLoading={isPurchasing}
                                /* Mock: gradient pill, size 16, weight 600, title-case.
                                   cta gives gradient/text-base/title-case; override radius→pill + weight→600. */
                                className="w-full sm:w-auto !rounded-full !font-semibold"
                            >
                                {isPurchasing ? 'Processando...' : 'Prosseguir para Pagamento'}
                            </Button>
                        </div>
                    </Card>

                    {/* Credit Info */}
                    <Card className="p-8">
                        <h2 className="text-2xl font-semibold text-snapfy-text-primary mb-6">Como Funcionam os Créditos</h2>
                        <ul className="space-y-4 text-base">
                            <CreditInfoItem title="Créditos Gratuitos">
                                Novas contas recebem 10 créditos iniciais válidos por 7 dias, o suficiente para gerar 10 imagens.
                            </CreditInfoItem>
                            <CreditInfoItem title="Custo de Créditos">
                                Cada imagem gerada com sucesso custa 1 crédito. Você só é cobrado por imagens geradas com sucesso.
                            </CreditInfoItem>
                            <CreditInfoItem title="Consumo de Créditos">
                                Os créditos são consumidos na ordem FIFO com base na data de expiração, com créditos promocionais usados primeiro.
                            </CreditInfoItem>
                            <CreditInfoItem title="Validade dos Créditos">
                                Os créditos adquiridos permanecem válidos pelo período especificado em cada pacote (de 15 dias a 5 anos, dependendo do pacote).
                            </CreditInfoItem>
                        </ul>
                    </Card>
                </>
            )}

            {/* History Tab */}
            {activeTab === 'history' && (
                <div className="space-y-4">
                    {/* Filters */}
                    <Card className="p-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <Input
                                type="date"
                                label="Data Início"
                                value={historyFilters.startDate}
                                onChange={(e) => {
                                    setHistoryFilters({ ...historyFilters, startDate: e.target.value });
                                    setHistoryPage(1);
                                }}
                            />
                            <Input
                                type="date"
                                label="Data Fim"
                                value={historyFilters.endDate}
                                onChange={(e) => {
                                    setHistoryFilters({ ...historyFilters, endDate: e.target.value });
                                    setHistoryPage(1);
                                }}
                            />
                            <div className="flex flex-col gap-1">
                                <span className="text-xs text-snapfy-text-secondary">Status</span>
                                <Select
                                    options={historyStatusOptions}
                                    value={historyFilters.status}
                                    onChange={(value) => {
                                        setHistoryFilters({ ...historyFilters, status: value });
                                        setHistoryPage(1);
                                    }}
                                    placeholder="Todos"
                                />
                            </div>
                            <div className="flex items-end">
                                <Button
                                    variant="ghost"
                                    onClick={() => {
                                        setHistoryFilters({
                                            startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
                                            endDate: new Date().toISOString().split('T')[0],
                                            status: '',
                                        });
                                        setHistoryPage(1);
                                    }}
                                    className="w-full"
                                >
                                    Resetar Filtros
                                </Button>
                            </div>
                        </div>
                    </Card>

                    {/* Table */}
                    <Card className="p-6">
                        <h2 className="text-xl font-semibold text-snapfy-text-primary mb-4">Histórico de Faturamento</h2>
                        {isLoadingHistory ? (
                            <div className="flex justify-center py-8">
                                <svg className="animate-spin h-8 w-8 text-snapfy-primary" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-snapfy-border text-sm text-snapfy-text-secondary">
                                            <th className="py-3 px-4 font-medium">Data</th>
                                            <th className="py-3 px-4 font-medium">Descrição</th>
                                            <th className="py-3 px-4 font-medium">Créditos</th>
                                            <th className="py-3 px-4 font-medium text-right">Tipo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {history.length > 0 ? history.map(tx => (
                                            <tr key={tx.id} className="border-b border-snapfy-border hover:bg-snapfy-surface-hover/50">
                                                <td className="py-4 px-4 text-snapfy-text-primary">{new Date(tx.date).toLocaleDateString('pt-BR')}</td>
                                                <td className="py-4 px-4 text-snapfy-text-secondary">{tx.description}</td>
                                                <td className="py-4 px-4 text-snapfy-text-primary">
                                                    {new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(tx.amount)} {Math.abs(tx.amount) === 1 ? 'crédito' : 'créditos'}
                                                </td>
                                                <td className="py-4 px-4 text-right">
                                                    <Badge
                                                        variant={
                                                            tx.status === 'expiration' ? 'error' :
                                                            tx.status === 'purchase' || tx.status === 'paid' ? 'success' :
                                                            tx.status === 'initial_grant' || tx.status === 'admin_grant' ? 'info' :
                                                            tx.status === 'consumption' || tx.status === 'subscription_grant' ? 'info' :
                                                            'info'
                                                        }
                                                    >
                                                        {tx.status === 'consumption' ? 'Consumo' :
                                                         tx.status === 'purchase' || tx.status === 'paid' ? 'Compra' :
                                                         tx.status === 'initial_grant' ? 'Bônus inicial' :
                                                         tx.status === 'admin_grant' ? 'Crédito manual' :
                                                         tx.status === 'subscription_grant' ? 'Assinatura' :
                                                         tx.status === 'expiration' ? 'Expirado' :
                                                         tx.status}
                                                    </Badge>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={4} className="py-8 px-4 text-center text-snapfy-text-secondary">
                                                    Nenhum histórico encontrado no período selecionado.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Pagination */}
                        {historyTotalPages > 1 && (
                            <div className="flex justify-center gap-2 mt-6">
                                <Button
                                    variant="ghost"
                                    onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                                    disabled={historyPage === 1}
                                >
                                    Anterior
                                </Button>
                                <span className="px-4 py-2 text-snapfy-text-secondary">
                                    {historyPage} / {historyTotalPages}
                                </span>
                                <Button
                                    variant="ghost"
                                    onClick={() => setHistoryPage(p => Math.min(historyTotalPages, p + 1))}
                                    disabled={historyPage === historyTotalPages}
                                >
                                    Próxima
                                </Button>
                            </div>
                        )}
                    </Card>
                </div>
            )}

            {/* Security Tab — Change Password */}
            {activeTab === 'security' && (
                <Card className="p-6 max-w-md">
                    <h3 className="text-lg font-semibold text-snapfy-text-primary mb-4">Alterar Senha</h3>
                    <div className="space-y-4">
                        {/* Password inputs kept as native <input> elements because the Input
                            primitive does not forward refs. These fields have no ref usage here
                            (state-driven via onChange), so they CAN use the Input primitive safely. */}
                        <Input
                            type="password"
                            label="Senha atual"
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                            error={passwordErrors.currentPassword}
                        />
                        <Input
                            type="password"
                            label="Nova senha"
                            value={passwordData.newPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                            error={passwordErrors.newPassword}
                        />
                        <Input
                            type="password"
                            label="Confirmar nova senha"
                            value={passwordData.newPasswordConfirmation}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, newPasswordConfirmation: e.target.value }))}
                            error={passwordErrors.newPasswordConfirmation}
                        />
                        <Button
                            variant="solid"
                            onClick={handleChangePassword}
                            disabled={isChangingPassword || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.newPasswordConfirmation}
                            isLoading={isChangingPassword}
                            className="w-full"
                        >
                            {isChangingPassword ? 'Alterando...' : 'Alterar Senha'}
                        </Button>
                    </div>
                </Card>
            )}
            {activeTab === 'security' && <PrivacySettingsCard />}

            {/* Billing Modal — kept as native overlay (not Modal primitive) because:
                1. The form holds unsaved data; explicit-close-only behavior must be preserved.
                2. The form is complex (9+ fields, CEP async fetch) and the Modal primitive's
                   built-in title/X header would alter the layout. Restyled with tokens only. */}
            {showBillingModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
                    <div className="bg-snapfy-surface rounded-card p-8 max-w-2xl w-full mx-4 border border-snapfy-border shadow-card">
                        <h2 className="text-2xl font-bold text-snapfy-text-primary mb-4">
                            {hasCompleteBillingInfo ? 'Editar Dados' : 'Dados para Nota Fiscal'}
                        </h2>
                        <p className="text-snapfy-text-secondary mb-6">
                            {hasCompleteBillingInfo
                                ? 'Atualize seus dados cadastrais:'
                                : 'Para emitir a nota fiscal, precisamos dos seus dados completos:'}
                        </p>

                        <div className="space-y-4">
                            {/* Dados Pessoais */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    type="text"
                                    label="CPF *"
                                    value={formatCPF(billingData.document)}
                                    onChange={(e) => setBillingData({ ...billingData, document: e.target.value.replace(/\D/g, '') })}
                                    placeholder="000.000.000-00"
                                    maxLength={14}
                                />
                                <Input
                                    type="text"
                                    label="Telefone *"
                                    value={formatPhone(billingData.phone)}
                                    onChange={(e) => setBillingData({ ...billingData, phone: e.target.value.replace(/\D/g, '') })}
                                    placeholder="(00) 00000-0000"
                                    maxLength={15}
                                />
                            </div>

                            {/* Separador */}
                            <div className="border-t border-snapfy-border pt-4">
                                <h3 className="text-lg font-semibold text-snapfy-text-primary mb-3">Endereço</h3>
                            </div>

                            {/* CEP + Logradouro — CEP has async spinner; kept native input with token classes
                                because the spinner absolute-positioned inside the field conflicts with
                                Input primitive's trailingAffordance slot (spinner is a loading state,
                                not a static affordance). */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-snapfy-text-secondary mb-2">
                                        CEP *
                                    </label>
                                    <div className="relative">
                                        <input
                                            type="text"
                                            value={formatCEP(billingData.postalCode)}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, '');
                                                setBillingData({ ...billingData, postalCode: value });
                                                if (value.length === 8) {
                                                    fetchAddressByCep(value);
                                                }
                                            }}
                                            placeholder="00000-000"
                                            className="w-full px-3 py-2.5 bg-snapfy-surface-hover border border-snapfy-border rounded-input text-snapfy-text-primary text-sm focus:border-snapfy-primary focus:ring-snapfy-focus outline-none placeholder:text-snapfy-text-secondary transition-[box-shadow,border-color] duration-150"
                                            maxLength={9}
                                        />
                                        {isLoadingCep && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                <svg className="animate-spin h-5 w-5 text-snapfy-primary" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <Input
                                        type="text"
                                        label="Logradouro *"
                                        value={billingData.address}
                                        onChange={(e) => setBillingData({ ...billingData, address: e.target.value })}
                                        placeholder="Rua, Avenida, etc."
                                    />
                                </div>
                            </div>

                            {/* Número e Complemento */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Input
                                    type="text"
                                    label="Número *"
                                    value={billingData.addressNumber}
                                    onChange={(e) => setBillingData({ ...billingData, addressNumber: e.target.value })}
                                    placeholder="123"
                                />
                                <div className="md:col-span-2">
                                    <Input
                                        type="text"
                                        label="Complemento"
                                        value={billingData.addressComplement}
                                        onChange={(e) => setBillingData({ ...billingData, addressComplement: e.target.value })}
                                        placeholder="Apto, Sala, etc. (opcional)"
                                    />
                                </div>
                            </div>

                            {/* Bairro, Cidade, Estado */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Input
                                    type="text"
                                    label="Bairro *"
                                    value={billingData.neighborhood}
                                    onChange={(e) => setBillingData({ ...billingData, neighborhood: e.target.value })}
                                    placeholder="Bairro"
                                />
                                <Input
                                    type="text"
                                    label="Cidade *"
                                    value={billingData.city}
                                    onChange={(e) => setBillingData({ ...billingData, city: e.target.value })}
                                    placeholder="Cidade"
                                />
                                <div className="flex flex-col gap-1">
                                    <span className="text-sm font-medium text-snapfy-text-secondary">Estado *</span>
                                    <Select
                                        options={ufOptions}
                                        value={billingData.state}
                                        onChange={(value) => setBillingData({ ...billingData, state: value })}
                                        placeholder="UF"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="ghost"
                                onClick={() => setShowBillingModal(false)}
                                className="flex-1"
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="solid"
                                onClick={handleSaveBillingInfo}
                                disabled={
                                    !billingData.document ||
                                    !billingData.phone ||
                                    !billingData.postalCode ||
                                    !billingData.address ||
                                    !billingData.addressNumber ||
                                    !billingData.neighborhood ||
                                    !billingData.city ||
                                    !billingData.state ||
                                    isSavingBilling
                                }
                                isLoading={isSavingBilling}
                                className="flex-1"
                            >
                                {isSavingBilling ? 'Salvando...' : (hasCompleteBillingInfo ? 'Atualizar Dados' : 'Salvar e Continuar')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </motion.div>
    );
};

export default ProfilePage;
